import { Injectable } from '@angular/core';
import { FactoryService, PersistentStorageService, InputAppMetadataDBFacade } from '@monorepo/core-data';
import { AppNotificationUtilClass } from '@monorepo/services';
import { act, Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import {
  LoadMasterData,
  MasterDataActionTypes,
  MasterDataLoaded,
  loadMasterDataByMasterList,
} from './master-data-db.actions';
import { getNormalizedData } from './master-data.db';
import { TranslateService } from '@ngx-translate/core';
// import { getNormalizedData, prepareForNormalization } from './master-data.db';
import { IMasterDataListMap } from '@monorepo/types';

@Injectable({ providedIn: 'root' })
export class MasterDataDBEffects {
  constructor(
    private actions$: Actions,
    private persistentStorageService: PersistentStorageService,
    private appMasterDBFacade: AppMasterDBFacade,
    private factoryService: FactoryService,
    private appNotificaion: AppNotificationUtilClass,
    private translate: TranslateService,
    private metadataFacade: InputAppMetadataDBFacade,
  ) {}

  loadMasterData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadMasterData>(MasterDataActionTypes.LoadMasterData),
      switchMap(() =>
        this.appMasterDBFacade.getAppIdentifier().pipe(
          take(1),
          map((appName) => {
            if (appName) {
              switch (appName) {
                case 'fabric_inspection':
                  return ['fabric_knit_defect_list', 'fabric_woven_defect_list'];
                default:
                  return [] as string[];
              }
            } else {
              return [] as string[];
            }
          }),
          catchError(() => {
            return of([] as string[]);
          }),
        ),
      ),
      switchMap((defectlist) =>
        this.appMasterDBFacade.getClientInfo().pipe(map((clientInfo) => [defectlist, clientInfo] as const)),
      ),

      switchMap((v) =>
        from(this.appNotificaion.presentLoading(this.translate.instant('input_app_db_effects.syncingEvents'))).pipe(
          map((loaderEl) => [loaderEl, ...v] as const),
        ),
      ),
      switchMap(([loaderEl, defectlist, client_info]) => {
        return this.factoryService.getMasterDataList(client_info, defectlist).pipe(
          map((res) => [loaderEl, res.response] as const),
          catchError((err) => {
            console.log('Error fetching master Data list', err);
            return of([loaderEl, null] as const); // Move forward is network is down try to get shifts from cache
          }),
        );
      }),
      switchMap(([loaderEl, v]) => from(this.appNotificaion.dismissLoadingV2(loaderEl)).pipe(map(() => v))),
      switchMap((res) => (res ? from(this.persistentStorageService.set('[MATER_DATA][RESPONSE]', res)) : of(null))),
      switchMap(() => from(this.persistentStorageService.get('[MATER_DATA][RESPONSE]'))),
      map((masterDataList) => {
        if (masterDataList) {
          return new MasterDataLoaded(getNormalizedData(masterDataList));
        }
        return { type: 'tes' };
      }),
    ),
  );

  loadMasterDataByMasterList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<loadMasterDataByMasterList>(MasterDataActionTypes.loadMasterDataByMasterList),
      switchMap((action) => {
        // Update syncing status if needed
        if (action.updateSyncingStatus && action.masterList.length > 0) {
          action.masterList.forEach((item) => {
            this.metadataFacade.updateSyncingStatus(item, 'SYNCING');
          });
        }
        return this.appMasterDBFacade.getClientInfo().pipe(map((clientInfo) => [action, clientInfo] as const));
      }),
      switchMap(([action, clientInfo]) => {
        // Show loader if needed
        if (action.showLoader) {
          return from(
            this.appNotificaion.presentLoading(
              this.translate.instant('input_app_master_data_effects.loadingMasterDataList'),
            ),
          ).pipe(map((loaderEl) => [loaderEl, action, clientInfo] as const));
        }
        return of([null, action, clientInfo] as const);
      }),
      switchMap(([loaderEl, action, clientInfo]) => {
        return this.factoryService.getMasterDataList(clientInfo, action.masterList).pipe(
          map((res) => [loaderEl, action, res.response] as const),
          catchError((err) => {
            console.log('Error fetching master Data list', err);
            return of([loaderEl, action, null] as const);
          }),
        );
      }),
      switchMap(([loaderEl, action, newData]) => {
        // Update syncing status if needed
        if (action.updateSyncingStatus && action.masterList.length > 0) {
          const status = newData ? 'SYNCED' : 'FAILED';
          action.masterList.forEach((item) => {
            this.metadataFacade.updateSyncingStatus(item, status);
          });
        }
        // Dismiss loader if it was shown
        if (loaderEl) {
          return from(this.appNotificaion.dismissLoadingV2(loaderEl)).pipe(map(() => [action, newData] as const));
        }
        return of([action, newData] as const);
      }),
      switchMap(([action, newData]) => {
        // If no new data, just get existing data
        if (!newData) {
          this.appNotificaion.showPopup(
            this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.header'),
            this.translate.instant('input_app_master_data_effects.loadingMasterDataListError.message'),
          );
          return from(this.persistentStorageService.get('[MATER_DATA][RESPONSE]'));
        }
        // We have new data, merge with existing data
        return from(this.persistentStorageService.get('[MATER_DATA][RESPONSE]')).pipe(
          switchMap((existingData) => {
            // If no existing data, set the new data from api
            if (!existingData) {
              return from(this.persistentStorageService.set('[MATER_DATA][RESPONSE]', newData)).pipe(
                map(() => newData),
              );
            }
            // Find default bundles in both new and existing data
            const existingDefault = existingData.find((bundle) => bundle.req_id === 'default');
            const newDefault = newData.find((bundle) => bundle.req_id === 'default');

            // If there's no default in existing data or newData, return it unchanged
            if (!existingDefault || !newDefault) {
              return of(existingData);
            }

            // Create updated default bundle immutably
            const updatedDefault = {
              ...existingDefault,
              data: {
                ...existingDefault.data,
                ...action.masterList.reduce((acc, key) => {
                  if (newDefault?.data?.[key]) {
                    acc[key] = newDefault.data[key];
                  }
                  return acc;
                }, {} as IMasterDataListMap),
              },
            };

            // Create the final merged array (replace only the default bundle)
            const updatedData = existingData.map((bundle) => (bundle.req_id === 'default' ? updatedDefault : bundle));

            return from(this.persistentStorageService.set('[MATER_DATA][RESPONSE]', updatedData)).pipe(
              map(() => updatedData),
            );
          }),
        );
      }),
      map((masterDataList) => {
        if (masterDataList) {
          return new MasterDataLoaded(getNormalizedData(masterDataList));
        }
        return { type: 'tes' };
      }),
    ),
  );
}
